import { Suspense, lazy, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Logo } from "./components/Logo";
import { ToastProvider } from "./components/Toast";
import { AuthProvider, useAuth } from "./lib/auth";

/* ---------- lazy-loaded page chunks ---------- */
const SigningPage = lazy(() => import("./pages/SigningPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const SignIn = lazy(() => import("./pages/SignIn"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const NewCertificate = lazy(() => import("./pages/NewCertificate"));
const CommentsRegister = lazy(() => import("./pages/CommentsRegister"));
const CertificateEditor = lazy(() => import("./pages/CertificateEditor"));
const ExportPreview = lazy(() => import("./pages/ExportPreview"));
const P6Analyser = lazy(() => import("./pages/P6Analyser"));
const MonteCarlo = lazy(() => import("./pages/MonteCarlo"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

function Gate({ children }: { children: ReactNode }) {
  const { loading, error, authenticated } = useAuth();
  if (loading) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh" }}>
        <div style={{ textAlign: "center" }}>
          <Logo />
          <div className="muted" style={{ marginTop: 16 }}>
            Loading your workspace…
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh" }}>
        <div className="card" style={{ maxWidth: 420, textAlign: "center" }}>
          <Logo />
          <h3 style={{ margin: "18px 0 8px" }}>Couldn't load your session</h3>
          <p className="muted" style={{ fontSize: 14 }}>
            {error}
          </p>
          <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (!authenticated)
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <SignIn />
      </Suspense>
    );
  return <>{children}</>;
}

function LoadingSpinner() {
  return (
    <div style={{ display: "grid", placeItems: "center", height: "100vh" }}>
      <div style={{ textAlign: "center" }}>
        <Logo />
        <div className="muted" style={{ marginTop: 16 }}>
          Loading…
        </div>
      </div>
    </div>
  );
}

/** Everything behind sign-in — the whole app except the public signing page. */
function AuthedApp() {
  return (
    <AuthProvider>
      <Gate>
        <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* AuthProvider has already spent the exchange token by now;
              this route just forwards on to the user's original target. */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/" element={<Dashboard />} />
          <Route path="/certificates/new" element={<NewCertificate />} />
          <Route path="/comments-register" element={<CommentsRegister />} />
          <Route path="/certificates/:id" element={<CertificateEditor />} />
          <Route path="/certificates/:id/export" element={<ExportPreview />} />
          <Route path="/p6" element={<P6Analyser />} />
          <Route path="/monte-carlo" element={<MonteCarlo />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </Gate>
    </AuthProvider>
  );
}

/**
 * The dedicated signing domain (sign.railassurance.co.uk) serves this same
 * Pages project; magic links there are `/{token}` at the root. On every other
 * host the signing page lives at /sign/:token (also kept on the signing host
 * for links sent before the domain move).
 */
const IS_SIGNING_HOST = window.location.hostname.split(".")[0] === "sign";

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <Routes>
          {/* Magic-link signing: signers have no account, so these routes live
              outside the auth gate entirely. */}
          <Route path="/sign/:token" element={<Suspense fallback={<LoadingSpinner />}><SigningPage /></Suspense>} />
          {IS_SIGNING_HOST ? (
            // Anything else on the signing host (including "/") renders the
            // page's invalid-link state rather than the authed app.
            <Route path="*" element={<Suspense fallback={<LoadingSpinner />}><SigningPage /></Suspense>} />
          ) : (
            <Route path="*" element={<AuthedApp />} />
          )}
        </Routes>
      </ToastProvider>
    </BrowserRouter>
  );
}
